import { LitElement, html } from "lit";

import { customElement, property, state } from "lit/decorators.js";

import { DataQueryController } from "ui-common/data-query-controller";
import { WowFormController } from "ui-common/form/wow-form-controller";
import "ui-components/button/wow-button";
import "ui-components/file-cropper/file-cropper";
import "ui-components/form-elements/input/wow-input";
import "ui-components/form-elements/textarea/textarea";
import "ui-components/modal/wow-modal";

import { worldFormModalStyles } from "./world-form-modal.css";

import { postsQueryOptions } from "../../queries/posts";
import { t } from "../../translations/i18n";
import "../posts-selection-grid/posts-selection-grid";

// Form data interface for form fields only
interface WorldFormData {
  name: string;
  thumbnailFileId?: string;
  selectedPostIds?: string[];
}

// Data interface for editing existing world
export interface WorldEditData {
  id: string;
  name: string;
  description?: string;
  thumbnailFileId?: string;
  mediaUrl?: string;
}

@customElement("world-form-modal")
export class WorldFormModal extends LitElement {
  static override styles = [worldFormModalStyles];

  @property({ type: String })
  mode: "create" | "edit" = "create";

  @property({ type: Object })
  data?: WorldEditData;

  @state()
  private isFormSubmitting = false;

  @state()
  private isThumbnailExpanded = false;

  @state()
  private selectedPostIds: string[] = [];

  private formController!: WowFormController<WorldFormData>;
  private postsController = new DataQueryController(this, () => postsQueryOptions);

  constructor() {
    super();
    this.initializeFormController();
  }

  private initializeFormController() {
    this.formController = new WowFormController<WorldFormData>(
      this,
      {
        name: this.data?.name || "",
        thumbnailFileId: this.data?.thumbnailFileId || undefined,
      },
      {
        validateOnBlur: true,
        validators: {
          name: [
            (value) => {
              if (!value || value.trim().length === 0) {
                return "World name is required";
              }
              if (value.trim().length > 64) {
                return "World name cannot exceed 64 characters";
              }
              return undefined;
            },
          ],
        },
      },
      (event) => this.handleFormSubmit(event),
    );
  }

  initSelectedPosts() {
    // Initialize pre-selected posts when editing a world and posts data becomes available
    if (
      this.mode === "edit" &&
      this.data?.id &&
      this.postsController.result.data?.posts &&
      !this.postsController.result.isPending &&
      this.selectedPostIds.length === 0
    ) {
      const postsInCurrentWorld = this.postsController.result.data.posts
        .filter((post) => post.worlds.some((world) => world.id === this.data!.id))
        .map((post) => post.id);

      if (postsInCurrentWorld.length > 0) {
        this.selectedPostIds = postsInCurrentWorld;
      }
    }
  }

  protected override willUpdate(changedProperties: Map<string | number | symbol, unknown>) {
    super.willUpdate(changedProperties);

    // Reinitialize form controller if data changes
    if (changedProperties.has("data") || changedProperties.has("mode")) {
      this.initializeFormController();
      // Set thumbnail expanded state when editing a world with media
      this.isThumbnailExpanded = this.mode === "edit" && !!this.data?.mediaUrl;
    }

    this.initSelectedPosts();
  }

  private get modalTitle() {
    return this.mode === "edit" ? t("editWorld.title") : t("createWorld.title");
  }

  private get saveButtonText() {
    if (this.isFormSubmitting) {
      return this.mode === "edit" ? "Saving..." : "Creating...";
    }
    return this.mode === "edit" ? t("editWorld.saveButton") : t("createWorld.createButton");
  }

  private handleModalClose = () => {
    this.dispatchEvent(
      new CustomEvent("modal-close", {
        bubbles: true,
        composed: true,
      }),
    );
  };

  private async handleFormSubmit(event: { data?: WorldFormData; isValid: boolean }) {
    if (!event.isValid || !event.data) return;

    this.isFormSubmitting = true;

    try {
      this.dispatchEvent(
        new CustomEvent("world-save", {
          bubbles: true,
          composed: true,
          detail: {
            mode: this.mode,
            data: {
              id: this.data?.id,
              name: event.data.name.trim(),
              thumbnailFileId: event.data.thumbnailFileId,
              postIds: this.selectedPostIds,
            },
          },
        }),
      );
    } catch (error) {
      console.error("Error saving world:", error);
    } finally {
      this.isFormSubmitting = false;
    }
  }

  private handlePostsSelectionChange = (event: CustomEvent) => {
    this.selectedPostIds = event.detail.selectedPostIds;
  };

  private handleSaveWorld = async () => {
    // Trigger form submission through form controller
    await this.formController.submit();
  };

  protected override render() {
    return html`
      <wow-modal
        variant="responsive"
        mobile-fullscreen
        @close=${this.handleModalClose}
        @footer-button-clicked=${this.handleSaveWorld}
      >
        <span slot="header">${this.modalTitle}</span>
        <span slot="footer-button-text">${this.saveButtonText}</span>

        <div class="modal-content">
          <form class="world-form">
            <div class="form-layout">
              <div class="left-column">
                <wow-input
                  name=${this.formController.fieldNames.name}
                  type="text"
                  placeholder="${t("createWorld.worldNamePlaceholder")}"
                  .value=${this.data?.name || ""}
                  required
                ></wow-input>

                <expandible-switch
                  ?isExpanded=${this.isThumbnailExpanded}
                  @expandedChange=${() => (this.isThumbnailExpanded = !this.isThumbnailExpanded)}
                  label=${t("createWorld.addThumbnail")}
                >
                  <file-cropper
                    name=${this.formController.fieldNames.thumbnailFileId}
                    currentFileUrl=${this.data?.mediaUrl}
                    slot="content"
                    viewport="world"
                    vpWidth="350"
                    vpHeight="140"
                    placeholder="test"
                  ></file-cropper>
                </expandible-switch>
              </div>

              <div class="right-column">
                <div class="posts-section">
                  <h3 class="posts-section-title">${t("posts.addPosts")}</h3>
                  <posts-selection-grid
                    .selectedPostIds=${this.selectedPostIds}
                    @posts-selection-change=${this.handlePostsSelectionChange}
                  ></posts-selection-grid>
                </div>

                <div class="selected-counter">
                  <span>${t("posts.selectedPosts")}</span>
                  <span>${this.selectedPostIds.length}</span>
                </div>
              </div>
            </div>
          </form>
        </div>
      </wow-modal>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "world-form-modal": WorldFormModal;
  }
}
